import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  MobileStepper,
  Fade,
  Zoom,
  Grow,
  Grid,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
} from '@mui/material';
import {
  WavingHandOutlined,
  ArticleOutlined,
  LocationOnOutlined,
  GroupOutlined,
  CheckCircleOutline,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  RocketLaunchOutlined,
  CancelOutlined,
} from '@mui/icons-material';
import { ContainedButton, LoadingButton, TextButton } from '../../common/StyledButtons';
import Colors from '../../../constants/Colors';
import StyledPage from '../../common/StyledPage';
import { StyledAutocomplete, StyledTextField } from '../../common/StyledTextField';
import { withRouter } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers';
import { StyledDatePicker } from '../../common/StyledDatePicker';
import { submitSiteContent } from '../../../actions/internal/SiteActions';
import { getUserId } from '../../../selectors/UserSelectors';
import { ARTICLES } from '../../../constants/Common';

const AddSiteContentPage = ({ history, match }) => {
    const [activeStep, setActiveStep] = useState(0);
    const [fadeIn, setFadeIn] = useState(true);
    const [showWelcome, setShowWelcome] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [topic, setTopic] = useState('');
    const [brief, setBrief] = useState('');
    const [location, setLocation] = useState('');
    const [audience, setAudience] = useState('');
    const [publishOption, setPublishOption] = useState('');
    const [publishDate, setPublishDate] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submissionComplete, setSubmissionComplete] = useState(false);
    const [submissionSuccessful, setSubmissionSuccessful] = useState(false);
    const [submissionStage, setSubmissionStage] = useState('Doing some quick checks...');
    const [keyword, setKeyword] = useState('');
    const [articleType, setArticleType] = useState('');

    const [topicError, setTopicError] = useState(false);
    const [briefError, setBriefError] = useState(false);
    const [keywordError, setKeywordError] = useState(false);
    const [articleTypeError, setArticleTypeError] = useState(false);
    const [publishOptionError, setPublishOptionError] = useState(false);

    const siteId = match.params.site_id

    useEffect(() => {
        const timeout = setTimeout(() => setShowWelcome(true), 200);
        return () => clearTimeout(timeout);
    }, []);

    const validateFields = () => {
        // Reset all errors before validating
        setTopicError(false);
        setBriefError(false);
        setKeywordError(false);
        setArticleTypeError(false);
        setPublishOptionError(false);

        // Validation logic per step
        let isValid = true;

        if (activeStep === 0 && !topic.trim()) {
        setTopicError(true);
            return false;
        }

        if (activeStep === 1 && (!brief.trim() || brief.length < 50)) {
            setBriefError(true);
            return false;
        }

        if (activeStep === 2 && !keyword.trim()) {
            setKeywordError(true);
            return false;
        }

        if (activeStep === 3 && !articleType.trim()) {
            setArticleTypeError(true);
            return false;
        }

        if (activeStep === 4 && (!publishOption.trim() || (publishOption !== 'proofread' && publishOption !== 'auto'))) {
            setPublishOptionError(true);
            return false;
        }

        if (activeStep === 4 && publishOption === 'auto' && !publishDate) {
            setPublishOptionError(true);
            return false;
        }

        return true;
    }

    const handleNext = async () => {
        const isValid = validateFields();
    
        // Stop if validation fails
        if (!isValid) return;
    
        // Continue to next step if valid
        if (activeStep === steps.length - 1) return;
        setFadeIn(false);
        setTimeout(() => {
        setActiveStep((prev) => prev + 1);
        setFadeIn(true);
        }, 300);
    };

  const handleBack = () => {
    if (activeStep === 0) {
        history.goBack();
        return;
    }
    setFadeIn(false);
    setTimeout(() => {
      setActiveStep((prev) => prev - 1);
      setFadeIn(true);
    }, 300);
  };
  
  const handleSubmitContent = async () => {
    try {
        const content = {
            topic: topic.trim(),
            brief: brief.trim(),
            primary_keyword: keyword.trim(),
            article_type: articleType.trim(),
            target_location: location.trim() || null,
            target_audience: audience.trim() || null,
          };
      
          const options = {
            auto_publish: publishOption === 'auto',
            publish_date: publishOption === 'auto' && publishDate ? new Date(publishDate).getTime() : null,
          };

          setIsLoading(true);
          await submitSiteContent(getUserId(), siteId, content, options);
          setSubmissionSuccessful(true);
    } catch(ex) {
        setSubmissionSuccessful(false);
    } finally {
        setIsLoading(false);
    }
  }

  const handleSubmit = async () => {
    const isValid = validateFields();
    if (!isValid) return;

    setIsSubmitting(true);
    setSubmissionStage('Checking a few things real quick.');

    setTimeout(() => setSubmissionStage('Putting some final touches on your brief.'), 1000);
    setTimeout(() => {
        setSubmissionStage('Uploading...')
    }, 4500);
    setTimeout(() => {
      setSubmissionStage('A few more moments.');
      setSubmissionComplete(true);
    }, 7000);

    // Send out request
    handleSubmitContent();


  };

  const renderWelcomeSlide = () => {
    const maxChars = 120;
    const charCount = topic.length;
    const isTooLong = charCount > maxChars;
  
    return (
      <Box textAlign="center" p={3}>
        <Zoom in={showWelcome} timeout={300}>
          <WavingHandOutlined sx={{ fontSize: 60, color: Colors.primaryColor }} />
        </Zoom>
        <Grow in={showWelcome} timeout={500} style={{ transitionDelay: '200ms' }}>
          <Box>
            <h3 style={{ fontSize: '28px' }}>
              Let's create some real content
            </h3>
            <p>
              Kick things off by choosing what you would like us to write about.
            </p>
          </Box>
        </Grow>
  
        <Box mt={3} position="relative" width="100%" maxWidth={600} mx="auto">
        <StyledTextField
            fullWidth
            label="Topic"
            placeholder="Example: Missouri hikes worth breaking a sweat for"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            helperText={
                topicError
                ? 'Please enter a topic.'
                : isTooLong
                ? `Maximum ${maxChars} characters allowed.`
                : 'Keep it short and clear (Hint: under 100 characters works best.)'
            }
            error={topicError || isTooLong}
            />
          <Box
            sx={{
              textAlign: 'right',
              fontSize: '13px',
              color: isTooLong ? 'error.main' : '#888',
              mt: 0.5,
            }}
          >
            {charCount} / {maxChars}
          </Box>
        </Box>
      </Box>
    );
  };
  
  const renderBriefSlide = () => {
    const minChars = 50;
    const maxChars = 2500;
    const charCount = brief.length;
    const isTooShort = charCount < minChars;
    const isTooLong = charCount > maxChars;
  
    return (
      <Box textAlign="center" p={3}>
        <ArticleOutlined sx={{ fontSize: 60, color: Colors.primaryColor }} />
        <h3 style={{ fontSize: '24px' }}>
          Next, tell us more about: <b>{topic || 'your topic'}</b>
        </h3>
        <p>
            Write a short overview or creative brief for this piece. Just a few notes will help guide the article. 
        <br />
        </p>
        <Box mt={3} position="relative">
        <StyledTextField
            fullWidth
            multiline
            minRows={4}
            label="Brief"
            value={brief}
            onChange={(e) => setBrief(e.target.value)}
            placeholder="Example: I want this article to be about..."
            helperText={
                briefError
                ? 'Please enter a short brief (at least 50 characters).'
                : isTooShort
                ? `Minimum ${minChars} characters required.`
                : isTooLong
                ? `Maximum ${maxChars} characters allowed.`
                : ''
            }
            error={briefError || isTooShort || isTooLong}
            />
          <Box
            sx={{
              textAlign: 'right',
              fontSize: '13px',
              color: isTooLong ? 'error.main' : '#888',
              mt: 0.5,
            }}
          >
            {charCount} / {maxChars}
          </Box>
        </Box>
      </Box>
    );
  };
  
  const renderDetailsSlide = () => {
    const articleOptions = Object.entries(ARTICLES).map(([key, a]) => ({
      key, // e.g. HOW_TO_GUIDES
      label: a.display_name,
      description: a.description,
    }));
  
    // Find the selected option object for display
    const selectedOption = articleOptions.find((opt) => opt.key === articleType) || null;
  
    return (
      <Box textAlign="center" p={3}>
        <LocationOnOutlined sx={{ fontSize: 60, color: Colors.primaryColor }} />
        <h3 style={{ fontSize: '24px' }}>
          Let's Personalize Your Article
        </h3>
        <p>
          Pick the kind of piece you want us to create and share who it should speak to.
        </p>
  
        <Grid container spacing={2} sx={{ mt: 2 }}>
          {/* ARTICLE TYPE AUTOCOMPLETE */}
          <Grid item xs={12} md={12}>
            <StyledAutocomplete
              options={articleOptions}
              label="Article Type"
              placeholder="Example: How-to Guides"
              getOptionLabel={(option) => option.label}
              value={selectedOption}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box>
                    <Typography sx={{ fontWeight: 600 }}>{option.label}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {option.description}
                    </Typography>
                  </Box>
                </li>
              )}
              onChange={(_, newValue) => {
                setArticleType(newValue ? newValue.key : '');
              }}
              error={articleTypeError}
              helperText={articleTypeError ? 'Please select an article type.' : ''}
            />
          </Grid>
  
          {/* LOCATION FIELD */}
          <Grid item xs={12} md={12}>
            <StyledTextField
              fullWidth
              label="Location (Optional)"
              placeholder="Example: Schitt’s Creek, Ontario"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
            />
          </Grid>
  
          {/* AUDIENCE FIELD */}
          <Grid item xs={12} md={12}>
            <StyledTextField
              fullWidth
              multiline
              minRows={3}
              label="My Audience (Optional)"
              value={audience}
              placeholder="Example: I want this article to be written for..."
              onChange={(e) => setAudience(e.target.value)}
            />
          </Grid>
        </Grid>
      </Box>
    );
  };
  
  

  const renderPublishingSlide = () => (
    <Box textAlign="center" p={3}>
      <RocketLaunchOutlined sx={{ fontSize: 60, color: Colors.primaryColor }} />
      <h3 style={{ fontSize: '24px' }}>How would you like to publish this article?</h3>
      <p>
        {publishOptionError ? (
            <span style={{ color: Colors.redColor}}>
                Please select a publishing option.
            </span>
        )
        :
        "Choose whether you’d like to proofread it first or have BCK publish it automatically."
        }
        
      </p>
  
      <Box
        mt={3}
        display="flex"
        flexDirection="column"
        alignItems="center"
        gap={2}
        width="100%"
        maxWidth={600}
        mx="auto"
      >
        {[
          {
            key: 'proofread',
            title: 'Proofread First',
            description: 'Review and approve your article before it’s published.',
          },
          {
            key: 'auto',
            title: 'Auto Publish',
            description: 'Let BCK publish it automatically once it’s ready. At a date and time of your choosing.',
          },
        ].map(({ key, title, description }) => (
          <Paper
            key={key}
            elevation={publishOption === key ? 4 : 1}
            onClick={() => setPublishOption(key)}
            sx={{
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              p: 2,
              width: '100%',
              borderRadius: '12px',
              textAlign: 'left',
              border:
                publishOption === key
                  ? `2px solid ${Colors.primaryColor}`
                  : '1px solid #ddd',
              boxShadow:
                publishOption === key
                  ? '0px 4px 12px rgba(0,0,0,0.1)'
                  : '0px 2px 4px rgba(0,0,0,0.05)',
              '&:hover': {
                boxShadow: '0px 4px 10px rgba(0,0,0,0.1)',
              },
            }}
          >
            <h4 style={{ fontSize: '18px', padding: 0, marginTop: 0, marginBottom: 1 }}>
              {title}
            </h4>
            <p>
              {description}
            </p>
          </Paper>
        ))}
      </Box>
  
      {publishOption === 'auto' && (
        <Box mt={2} width="100%" display="flex" justifyContent="center">
          <Box width="100%">
            <StyledDatePicker
                fullWidth
              label="Select a publish date"
              views={['year', 'month', 'day', 'hours']}
              value={publishDate}
              onChange={(newDate) => setPublishDate(newDate)}
              error={publishOptionError}
              slotProps={{
                textField: { fullWidth: true, placeholder: 'Choose a date' },
              }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );

  const renderKeywordSlide = () => {
    const maxChars = 65;
    const charCount = keyword.length;
    const isTooLong = charCount > maxChars;
  
    return (
      <Box textAlign="center" p={3}>
        <ArticleOutlined sx={{ fontSize: 60, color: Colors.primaryColor }} />
        <h3 style={{ fontSize: '24px' }}>
          Choose your article’s focus phrase
        </h3>
        <p>
          What would someone type on Google to find this article? Give us one short phrase that sums it up.
        </p>
  
        <Box mt={3} width="100%" maxWidth={500} mx="auto" position="relative">
        <StyledTextField
            fullWidth
            label="Focus Phrase"
            placeholder="Example: best hiking trails in Missouri"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            helperText={
                keywordError
                ? 'Please enter a focus phrase.'
                : isTooLong
                ? `Maximum ${maxChars} characters allowed.`
                : 'Hint: Try a short, specific phrase (3-5 words).'
            }
            error={keywordError || isTooLong}
            />
  
          <Box
            sx={{
              textAlign: 'right',
              fontSize: '13px',
              color: isTooLong ? 'error.main' : '#888',
              mt: 0.5,
            }}
          >
            {charCount} / {maxChars}
          </Box>
        </Box>
      </Box>
    );
  };
  

  const steps = [
    renderWelcomeSlide,
    renderBriefSlide,
    renderKeywordSlide,
    renderDetailsSlide,
    renderPublishingSlide
  ];

  const renderStepContent = (step) => (
    <Fade in={fadeIn} timeout={400} key={`fade-${step}`}>
      <Paper
        elevation={1}
        sx={{
          p: 4,
          mt: 2,
          borderRadius: '12px',
          boxShadow: '0px 4px 12px rgba(0,0,0,0.05)',
        }}
      >
        {steps[step]()}
      </Paper>
    </Fade>
  );

  return (
<StyledPage>
    <Grid
      container
      spacing={3}
      justifyContent="center"
      alignItems="center"
      sx={{
        minHeight: '80vh',
        py: 4,
      }}
    >
      <Grid
        item
        xs={12}
        md={8}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
        }}
      >
        {/* NORMAL STEPPER FLOW */}
        {!isSubmitting && !submissionComplete && (
          <>
            {renderStepContent(activeStep)}
            <MobileStepper
              variant="dots"
              steps={steps.length}
              position="static"
              activeStep={activeStep}
              nextButton={
                <LoadingButton
                  onClick={
                    activeStep === steps.length - 1
                      ? handleSubmit
                      : handleNext
                  }
                  isLoading={isLoading}
                >
                  {activeStep === steps.length - 1 ? 'Submit' : 'Next'}
                </LoadingButton>
              }
              backButton={
                <TextButton onClick={handleBack}>
                  {activeStep === 0 ? 'Cancel' : 'Back'}
                </TextButton>
              }
              sx={{
                mt: 2,
                backgroundColor: 'transparent',
                width: '100%',
                maxWidth: '100%',
                px: 0,
                '& .MuiMobileStepper-dots': {
                  flex: 1,
                  justifyContent: 'center',
                },
              }}
            />
          </>
        )}

        {isSubmitting && !submissionComplete && !isLoading && (
        <Fade in timeout={400}>
            <Paper
            elevation={2}
            sx={{
                p: 5,
                textAlign: 'center',
                borderRadius: '12px',
                boxShadow: '0px 4px 12px rgba(0,0,0,0.05)',
            }}
            >
            <Zoom in timeout={400}>
                <RocketLaunchOutlined
                sx={{ fontSize: 70, color: Colors.primaryColor, mb: 2 }}
                />
            </Zoom>
            <h3 style={{ fontSize: '24px' }}>Submitting your brief...</h3>
                <p>
                {submissionStage}
                </p>
            <Box
                sx={{
                mt: 2,
                height: 6,
                width: '100%',
                backgroundColor: '#eee',
                borderRadius: '4px',
                overflow: 'hidden',
                }}
            >
                <Box
                sx={{
                    width: '100%',
                    height: '100%',
                    backgroundColor: Colors.primaryColor,
                    animation: 'progress 7s linear',
                    '@keyframes progress': {
                    '0%': { width: '0%' },
                    '100%': { width: '100%' },
                    },
                }}
                />
            </Box>
            </Paper>
        </Fade>
        )}

        {/* COMPLETION CARD */}
        {submissionComplete && !isLoading && submissionSuccessful && (
          <Fade in timeout={400}>
            <Paper
              elevation={2}
              sx={{
                p: 5,
                textAlign: 'center',
                borderRadius: '12px',
                boxShadow: '0px 4px 12px rgba(0,0,0,0.05)',
              }}
            >
              <Zoom in timeout={400}>
                <CheckCircleOutline
                  sx={{ fontSize: 80, color: Colors.primaryColor, mb: 2 }}
                />
              </Zoom>
                <h3 style={{ fontSize: '24px' }}>Brief Uploaded Successfully</h3>
                <p>
                Your brief has been uploaded and writing will start soon!  
                You can safely close this window or return to your dashboard.
                </p>
                <ContainedButton
                onClick={() => {
                    history.replace(`/dashboard/site/${siteId}/content`);
                }}
              >
                Close
              </ContainedButton>
            </Paper>
          </Fade>
        )}

        {/* COMPLETION CARD */}
        {submissionComplete && !isLoading && !submissionSuccessful && (
          <Fade in timeout={400}>
            <Paper
              elevation={2}
              sx={{
                p: 5,
                textAlign: 'center',
                borderRadius: '12px',
                boxShadow: '0px 4px 12px rgba(0,0,0,0.05)',
              }}
            >
                <Zoom in timeout={400}>
                    <CancelOutlined
                    sx={{ fontSize: 80, color: Colors.redColor, mb: 2 }}
                    />
                </Zoom>
                <h3 style={{ fontSize: '24px' }}>Something Went Wrong</h3>
                <p>
                Well, this is embarrasing... Please try submitting again. If you continue to run into this problem, please contact us!
                </p>
              <ContainedButton
                onClick={() => {
                    setSubmissionComplete(false);
                    setIsSubmitting(false);
                    setActiveStep(0);
                }}
              >
                Try Again
              </ContainedButton>
            </Paper>
          </Fade>
        )}
      </Grid>
    </Grid>
  </StyledPage>

  );
};

export default withRouter(AddSiteContentPage);
